import PropTypes from "prop-types";
import { useCallback } from "react";
import { OFFER_TYPES } from "app/constants";
import Button from "app/pages/.shared/form/Button";
import AmountContainer from "app/pages/.shared/AmountContainer.jsx";
import { FormattedDate, FormattedMessage, FormattedPlural } from "react-intl";
import get from "lodash/get";
import FrequencyFlyersMarketing from "@brand/FrequencyFlyersMarketing/FrequencyFlyersMarketing";
import TransportationPictoContainer from "app/pages/.shared/TransportationPicto/TransportationPictoContainer";
import RadioFormik from "app/pages/.shared/form/RadioFormik";
import AvailableDurations from "app/pages/.shared/AvailableDurations";
import PopoverInfo from "app/pages/.shared/Popover/PopoverInfo";

const OfferInput = ({
	offer = {},
	onChange = () => {},
	allAvailableDurations = [],
	productPriceValue,
	...props
}) => {
	const handleChange = useCallback(() => {
		onChange(offer.pricing, offer.negotiated);
	}, [offer.pricing, offer.negotiated]);

	const nightCount = get(offer, "pricing.minPrice.n");
	const offerType = get(offer, "pricing.type");
	const price = get(offer, "pricing.minPrice.p");
	const publicPrice = get(offer, "pricing.minPrice.pp");
	const priceIsAvailable = !isNaN(price);
	const isOfferWithFlight = offerType === OFFER_TYPES.FLIGHT_WITH_ACCOMMODATION;
	const departureDate = (
		<FormattedDate
			timeZone="UTC"
			day="2-digit"
			month="2-digit"
			year="numeric"
			value={offer?.pricing?.minPrice?.dd}
		/>
	);
	const departureCity = offer?.pricing?.minPrice?.dc;
	const availableDurations =
		allAvailableDurations.length > 1 && Number(productPriceValue) > 0 ? (
			<AvailableDurations durations={allAvailableDurations} />
		) : (
			""
		);

	// Stop the event(onClick) propagation from the parent element to prevent the PopoverInfo content from appearing
	const stopOnClickPropagation = event => {
		event.stopPropagation();
		event.preventDefault();
	};

	// eslint-disable-next-line react/no-multi-comp
	const nightCountWording = (...chunks) => (
		<span className="offer-input__night-count">{chunks}</span>
	);
	const nightCountWordingParameters = { count: nightCount };

	return (
		<RadioFormik
			showIcon={false}
			id={`offerType-${offerType}`}
			name="offerType"
			value={offerType}
			toggle={handleChange}
			data-testid={props["data-testid"]}
		>
			<div className="offer-input__wrapper">
				<div className="offer-input__content">
					<div className="offer-input__prices">
						<div className="offer-input__label">
							{get(offer, "pricing.label")}
							{offerType === OFFER_TYPES.FLIGHT_WITH_ACCOMMODATION && (
								<TransportationPictoContainer />
							)}
						</div>
						<div className="offer-input__price">
							<AmountContainer
								amount={price}
								prefix={<FormattedMessage id="offer.per.person.from.label" />}
								suffix={
									<FormattedMessage id="general.amount.per.person.suffix.label.short" />
								}
							/>
						</div>
						{offerType === OFFER_TYPES.ACCOMODATION_ONLY && (
							<div className="offer-input__public-price">
								{publicPrice && (
									<AmountContainer
										amount={publicPrice}
										prefix={<FormattedMessage id="general.instead.of.price" />}
										suffix={
											<FormattedMessage id="general.amount.per.person.suffix.label.short" />
										}
									/>
								)}
							</div>
						)}
						{priceIsAvailable && (
							<div
								className="offer-input__info-price"
								onClick={stopOnClickPropagation}
							>
								<FormattedPlural
									value={nightCount}
									one={
										<FormattedMessage
											id="general.nights.count.singular"
											values={nightCountWordingParameters}
										>
											{nightCountWording}
										</FormattedMessage>
									}
									other={
										<FormattedMessage
											id="general.nights.count"
											values={nightCountWordingParameters}
										>
											{nightCountWording}
										</FormattedMessage>
									}
								/>

								<PopoverInfo
									popOverContentClassname="offer-input__tooltip-content"
									popOverButtonClassName="offer-input__tooltip"
									popOverContent={
										isOfferWithFlight ? (
											<>
												<FormattedMessage
													id="product.page.cheapest.fa.offer.info"
													values={{
														duration: nightCount,
														departureCity,
														departureDate,
													}}
												/>
												{availableDurations}
											</>
										) : (
											<>
												<FormattedMessage
													id="product.page.cheapest.ao.offer.info"
													values={{
														duration: nightCount,
														departureDate,
													}}
												/>
												{availableDurations}
											</>
										)
									}
								/>
							</div>
						)}
					</div>
					<div className="offer-input__cta">
						<Button variant="primary">
							<FormattedMessage id="search.engine.offers.cta.button" />
						</Button>
					</div>
				</div>
				{offerType === OFFER_TYPES.FLIGHT_WITH_ACCOMMODATION && (
					<FrequencyFlyersMarketing />
				)}
			</div>
		</RadioFormik>
	);
};

OfferInput.propTypes = {
	field: PropTypes.object,
	productPriceValue: PropTypes.string,
	allAvailableDurations: PropTypes.arrayOf(PropTypes.number),
	offer: PropTypes.object,
	"data-testid": PropTypes.string,
	onChange: PropTypes.func,
};

export default OfferInput;
